import { ComponentProps } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { TextFieldInput } from '../../UNSAFE_TextField';
import { calcLength, CountUnit as _CountUnit, filter } from '../../utils/UNSAFE_lengthFilter';

export type CountUnit = _CountUnit;

type PickedPropsFromTextFieldInput = Pick<
  ComponentProps<typeof TextFieldInput>,
  'onCommit' | 'onInput' | 'value'
>;
type UseLengthFilterProps = PickedPropsFromTextFieldInput & {
  maxLength?: number;
  maxLengthUnit?: CountUnit;
};

/**
 * A custom hook that applies the length filter to text field input
 * @param param0 The props for the useLengthFilter hook
 * @returns The filtered event handlers
 */
export function useLengthFilter({
  maxLength,
  maxLengthUnit,
  onCommit,
  onInput,
  value
}: UseLengthFilterProps) {
  const hasNoValue = value === undefined;
  const [isMaxLengthExceeded, setMaxLengthExceeded] = useState(false);
  const parse = useCallback(
    (value: string) => filter(value, maxLength, maxLengthUnit),
    [maxLength, maxLengthUnit]
  );
  // Reapply maxLength filter when the value is changed
  useEffect(() => {
    if (hasNoValue) {
      return;
    }

    const filteredValue = parse(value);
    if (filteredValue !== value) {
      onInput?.({ previousValue: value, value: filteredValue });
      onCommit?.({ previousValue: value, value: filteredValue });

      // Value is filtered, means that the max length is exceeded
      setMaxLengthExceeded(true);
    } else {
      // Value is not filtered, means that the max length is not exceeded
      setMaxLengthExceeded(false);
    }
  }, [value, onCommit, onInput, parse, hasNoValue]);

  const onFilteredInput = useCallback(
    (...args: Parameters<Exclude<typeof onInput, undefined>>) => {
      const { previousValue, value } = args[0];
      const filteredValue = parse(value ?? '');

      // Call the onInput event only when the value is changed
      if (previousValue !== filteredValue) {
        onInput?.({ ...args[0], value: filteredValue });
        // value is not filtered, so the max length is not exceeded
        setMaxLengthExceeded(false);
      } else {
        // value is changed but then filtered to previous value
        // meaning that the max length was exceeded
        setMaxLengthExceeded(true);
      }
    },
    [onInput, parse]
  );

  const valueLength = hasNoValue ? undefined : calcLength(value, maxLengthUnit);
  // this is set to true when the parsed value length is exactly the maxlength
  const isMaxLengthReached = hasNoValue ? undefined : maxLength === valueLength;

  return {
    valueLength,
    isMaxLengthReached,
    isMaxLengthExceeded,
    onFilteredInput
  };
}
